"use client"

import React from "react"
import { Button } from "./button"
import { cn } from "@/lib/utils"

interface EnhancedButtonProps extends Omit<React.ComponentProps<typeof Button>, 'variant' | 'size'> {
  variant?: "hero-primary" | "hero-secondary" | "hero-outline" | "standard" | "compact" | "large"
  size?: "sm" | "md" | "lg" | "xl"
  icon?: React.ReactNode
  iconPosition?: "left" | "right"
  pulse?: boolean
}

export const EnhancedButton = React.forwardRef<
  HTMLButtonElement,
  EnhancedButtonProps
>(({
  className,
  variant = "standard",
  size = "md",
  icon,
  iconPosition = "left",
  pulse = false,
  children,
  ...props
}, ref) => {

  const baseClasses = "font-bold transition-all duration-300 focus:ring-4 focus:ring-green-200"

  const variantClasses = {
    "hero-primary": "group relative bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-2xl btn-tactile overflow-hidden",
    "hero-secondary": "group bg-white text-green-600 hover:bg-gray-50 shadow-xl btn-tactile",
    "hero-outline": "group border-2 border-white/30 text-white hover:bg-white/10",
    "standard": "bg-green-600 hover:bg-green-700 text-white shadow-lg btn-tactile",
    "compact": "bg-green-600 hover:bg-green-700 text-white shadow-md btn-tactile",
    "large": "bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-xl btn-tactile"
  }

  const sizeClasses = {
    sm: "px-4 py-2 text-sm rounded-lg min-h-[40px]",
    md: "px-6 py-3 text-base rounded-xl min-h-[48px]",
    lg: "px-8 py-4 text-lg rounded-full min-h-[56px]",
    xl: "px-12 py-6 text-xl rounded-full min-h-[64px]"
  }

  const iconClasses = {
    left: "mr-2 group-hover:scale-110 transition-transform duration-300",
    right: "ml-2 group-hover:translate-x-1 transition-transform duration-300"
  }

  const pulseClasses = pulse ? "animate-pulse-soft" : ""

  return (
    <Button
      ref={ref}
      className={cn(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        pulseClasses,
        className
      )}
      {...props}
    >
      <div className="relative flex items-center">
        {icon && iconPosition === "left" && (
          <span className={iconClasses.left}>{icon}</span>
        )}
        {children}
        {icon && iconPosition === "right" && (
          <span className={iconClasses.right}>{icon}</span>
        )}
      </div>
    </Button>
  )
})

EnhancedButton.displayName = "EnhancedButton"
